import { useState, useRef, useEffect } from 'react';
import { LoginPopup } from '../popups/LoginPopup';
import { SubscriptionPopup } from '../popups/SubscriptionPopup';
import { AccountPopup } from '../popups/AccountPopup';
import { SiteFooter } from '../components/SiteFooter';
import './Landing.css';

const D33P_CDN = 'https://genres.nyc3.cdn.digitaloceanspaces.com/d33p';
const VIDEO_CDN = 'https://genres.nyc3.cdn.digitaloceanspaces.com/claude';
const P0RN_CDN = 'https://genres.nyc3.cdn.digitaloceanspaces.com/p0rn';
const EU_AI_ACT_URL = 'https://www.europarl.europa.eu/news/sv/press-room/20260427IPR42011/ai-act-deal-on-simplification-measures-ban-on-nudifier-apps';

// Auto-looping video tiles right under the hero — keep it punchy, just the two heroes.
const HERO_TILES = [
    { key: 'cumshot', title: 'Cumshot', video: `${D33P_CDN}/deep_1779479885007.mp4`, badge: 'POPULAR' },
    { key: 'blowjob', title: 'Blowjob', video: `${D33P_CDN}/deep_1779479895386.mp4`, badge: 'HOT' },
];

// Example photo grid — vertical 3:4 portraits
const EXAMPLE_PHOTOS = [
    `${D33P_CDN}/d33p_1779478822557.png`,
    `${D33P_CDN}/d33p_1779478841407.png`,
    `${D33P_CDN}/d33p_1779478863084.png`,
    `${D33P_CDN}/d33p_1779478870625.png`,
];

const FEATURES = [
    { eyebrow: '[01] VIDEO', title: 'Generate video', body: 'Generate a photo, pick an action — get a 1080p HD clip in ~2 minutes. No uploads.', cta: 'Generate video', tone: 'acid', target: 'video' },
    { eyebrow: '[02] PHOTO', title: 'Generate photo', body: 'Build a photo from scratch. Pose, persona, scene — write a prompt, hit go.', cta: 'Generate photo', tone: 'pink', target: 'photo' },
];

const USE_CASES = [
    { emoji: '🎬', label: 'Video' },
    { emoji: '📸', label: 'Photo' },
    { emoji: '💦', label: 'Cumshot' },
    { emoji: '👄', label: 'Blowjob' },
    { emoji: '🤠', label: 'Cowgirl' },
    { emoji: '🏝️', label: 'Anywhere' },
];

const STEPS = [
    { num: '01', title: 'Pick a preset', body: 'Generate a girl from scratch with a prompt — pose, look, scene, all yours.', emoji: '✨' },
    { num: '02', title: 'Choose what to make', body: 'Pick an action for a video, or describe the scene for a photo.', emoji: '🎯' },
    { num: '03', title: 'Get the result', body: '~80s for an HD photo. ~2 min for a 1080p video. Download, remix, animate.', emoji: '⚡' },
];

const FAQS = [
    { q: 'Is it really uncensored?', a: 'Yes — no NSFW filter, no watermarks, no review queue. The output is yours.' },
    { q: 'How long does generation take?', a: 'About 80 seconds for a photo, ~2 minutes for an HD video. Premium gets priority queue.' },
    { q: 'Can I upload my own photos?', a: 'No. We don\'t accept user-uploaded images. Everything you animate is generated by D33P from prompts. This keeps us compliant with the EU AI Act ban on nudifier apps — no real-person photos enter the pipeline.' },
    { q: 'Do tokens expire?', a: 'No. Top-up once and burn through them whenever.' },
    { q: 'Is there a free tier?', a: 'New accounts get a trial. After that, tokens start at $9.99.' },
    { q: 'Do you have an undressing / nudifier tool?', a: 'No. We comply with the EU AI Act ban on nudifier apps. D33P only generates synthetic content from text prompts — no user uploads, no real persons depicted.' },
];

function HeaderMenu({ user, onNavigate, onSignOut }) {
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return;
        const onDocDown = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
        document.addEventListener('mousedown', onDocDown);
        document.addEventListener('touchstart', onDocDown);
        document.addEventListener('keydown', onKey);
        return () => {
            document.removeEventListener('mousedown', onDocDown);
            document.removeEventListener('touchstart', onDocDown);
            document.removeEventListener('keydown', onKey);
        };
    }, [open]);

    return (
        <div className="lndHeaderMenu" ref={wrapRef}>
            <button
                className={`lndHeaderMenuBtn${open ? ' lndHeaderMenuBtn--open' : ''}`}
                onClick={() => setOpen(o => !o)}
                aria-label="More menu"
                aria-expanded={open}
            >
                <span aria-hidden="true">⋯</span>
            </button>
            {open && (
                <div className="lndHeaderMenuDropdown" role="menu">
                    <button className="lndHeaderMenuItem lndHeaderMenuItem--admin" role="menuitem" onClick={() => { setOpen(false); onNavigate('video'); }}>Generate video</button>
                    <button className="lndHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('photo'); }}>Generate photo</button>
                    <button className="lndHeaderMenuItem" role="menuitem" onClick={() => { setOpen(false); onNavigate('gallery'); }}>Gallery</button>
                    {user && (
                        <button
                            className="lndHeaderMenuItem lndHeaderMenuItem--danger"
                            role="menuitem"
                            onClick={() => { setOpen(false); onSignOut(); }}
                        >
                            Sign out
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}

function useInViewport(ref, rootMargin = '120px') {
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(([entry]) => setInView(entry.isIntersecting), { rootMargin });
        io.observe(el);
        return () => io.disconnect();
    }, [ref, rootMargin]);
    return inView;
}

function HeroTile({ tile, onClick }) {
    const ref = useRef(null);
    const videoRef = useRef(null);
    const inView = useInViewport(ref, '600px');

    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (inView) v.play().catch(() => {});
        else v.pause();
    }, [inView]);

    return (
        <button ref={ref} className="lndTile" onClick={onClick}>
            <video
                ref={videoRef}
                className="lndTileVideo"
                src={tile.video}
                muted playsInline loop autoPlay
                preload="auto"
            />
            {tile.badge && <span className={`lndTileBadge lndTileBadge--${tile.badge}`}>{tile.badge}</span>}
            <span className="lndTileLabel">{tile.title}</span>
        </button>
    );
}

function FaqItem({ q, a }) {
    const [open, setOpen] = useState(false);
    return (
        <button className={`lndFaqItem${open ? ' lndFaqItem--open' : ''}`} onClick={() => setOpen(o => !o)}>
            <span className="lndFaqQ">
                <span>{q}</span>
                <span className="lndFaqToggle" aria-hidden="true">{open ? '–' : '+'}</span>
            </span>
            {open && <span className="lndFaqA">{a}</span>}
        </button>
    );
}

// Looping preview clip used on the "Generate video" showcase right side.
function VideoShowcaseClip({ src }) {
    const ref = useRef(null);
    const videoRef = useRef(null);
    const inView = useInViewport(ref, '300px');
    useEffect(() => {
        const v = videoRef.current;
        if (!v) return;
        if (inView) v.play().catch(() => {});
        else v.pause();
    }, [inView]);
    return (
        <div ref={ref} className="lndVidShowcaseClip">
            <video
                ref={videoRef}
                src={src}
                muted playsInline loop
                preload="metadata"
            />
        </div>
    );
}

export function Landing({ token, user, setSignedIn, onNavigate }) {
    const [showLogin, setShowLogin] = useState(false);
    const [showPricing, setShowPricing] = useState(false);
    const [showAccount, setShowAccount] = useState(false);

    const signOut = () => {
        localStorage.removeItem('igenfuntoken');
        window.location.reload();
    };

    const goVideo = () => onNavigate('video');
    const goPhoto = () => onNavigate('photo');
    const handleBuy = () => {
        if (!user) { setShowLogin(true); return; }
        setShowPricing(true);
    };

    return (
        <div className="lndPage">
            {/* Header */}
            <div className="lndHeader">
                <div className="lndHeaderLeft">
                    <span className="lndLogo" onClick={() => onNavigate('landing')}>D33P</span>
                    <nav className="lndNav">
                        <button className="lndNavBtn lndNavBtn--admin" onClick={() => onNavigate('video')}>Generate video</button>
                        <button className="lndNavBtn" onClick={() => onNavigate('photo')}>Generate photo</button>
                        <button className="lndNavBtn" onClick={() => onNavigate('gallery')}>Gallery</button>
                    </nav>
                </div>
                <div className="lndHeaderRight">
                    {!user ? (
                        <button className="lndLoginBtn" onClick={() => setShowLogin(true)}>Log in</button>
                    ) : (
                        <>
                            <button className="lndCredits" onClick={() => setShowAccount(true)}>{user.credits ?? 0} tokens</button>
                            <button className="lndSignOutBtn lndSignOutBtn--desktopOnly" onClick={signOut}>Sign out</button>
                        </>
                    )}
                    <HeaderMenu user={user} onNavigate={onNavigate} onSignOut={signOut} />
                </div>
            </div>

            {/* Hero */}
            <section className="lndHero">
                <span className="lndHeroPill">[ AI NSFW TOOL ]</span>
                <h1 className="lndHeroTitle">
                    <span className="lndHeroTitleAccent">Generate</span> NSFW
                    <br />
                    <span className="lndHeroTitleQuiet">videos &amp; photos</span>
                </h1>
                <p className="lndHeroSubtitle">
                    Pick an action, generate a photo, or write a prompt — get a 1080p clip or HD photo in seconds. Fully uncensored, fully synthetic, zero uploads.
                </p>
                <div className="lndHeroCtas">
                    <button className="lndHeroBtn lndHeroBtn--primary" onClick={goVideo}>
                        <span className="lndHeroBtnIcon" aria-hidden="true">▶</span>
                        Generate a video
                    </button>
                    <button className="lndHeroBtn lndHeroBtn--ghost" onClick={goPhoto}>
                        Generate a photo →
                    </button>
                </div>
                <div className="lndHeroStats">
                    <span className="lndHeroStat"><strong>~80s</strong> per photo</span>
                    <span className="lndHeroStatDot">·</span>
                    <span className="lndHeroStat"><strong>1080p</strong> HD video</span>
                    <span className="lndHeroStatDot">·</span>
                    <span className="lndHeroStat"><strong>0</strong> moderation</span>
                </div>
            </section>

            {/* Hero auto-play tiles */}
            <section className="lndTiles">
                {HERO_TILES.map(t => (
                    <HeroTile key={t.key} tile={t} onClick={goVideo} />
                ))}
            </section>

            {/* Generate Video showcase — image → video */}
            <section className="lndVidShowcase">
                <div className="lndVidShowcaseGrid">
                    <div className="lndVidShowcaseLeft">
                        <span className="lndEyebrow lndEyebrow--ink">[ FLAGSHIP ]</span>
                        <h2 className="lndVidShowcaseTitle">
                            <span className="lndVidShowcaseTitleAccent">Animate</span> her.
                        </h2>
                        <p className="lndVidShowcaseBody">
                            Generate a photo right on D33P, pick an action — Cumshot, Cowgirl, Blowjob, and 8 more — and get a 1080p HD clip in about two minutes. No uploads, ever.
                        </p>
                        <ul className="lndVidShowcaseList">
                            <li><span className="lndUndressMark">✦</span> 100% synthetic, no uploads</li>
                            <li><span className="lndUndressMark">✦</span> 11 ready-made actions</li>
                            <li><span className="lndUndressMark">✦</span> 1080p HD, no watermark</li>
                        </ul>
                        <button className="lndHeroBtn lndHeroBtn--primary" onClick={goVideo}>
                            Generate video
                            <span aria-hidden="true">↗</span>
                        </button>
                    </div>

                    <div className="lndVidShowcaseRight">
                        <figure className="lndVidShowcasePane lndVidShowcasePane--photo">
                            <img src={`${VIDEO_CDN}/photo/cumshot_photo.png`} alt="Source" />
                            <figcaption>PHOTO</figcaption>
                        </figure>
                        <span className="lndVidShowcaseArrow" aria-hidden="true">→</span>
                        <figure className="lndVidShowcasePane lndVidShowcasePane--video">
                            <VideoShowcaseClip src={`${P0RN_CDN}/cumshot_small.mp4`} />
                            <figcaption>VIDEO</figcaption>
                        </figure>
                    </div>
                </div>
            </section>

            {/* Use case strip */}
            <section className="lndStrip">
                {USE_CASES.map(u => (
                    <span key={u.label} className="lndStripItem">
                        <span className="lndStripEmoji" aria-hidden="true">{u.emoji}</span>
                        <span className="lndStripLabel">{u.label}</span>
                    </span>
                ))}
            </section>

            {/* How it works */}
            <section className="lndHow">
                <div className="lndSectionHead">
                    <span className="lndEyebrow">[ HOW IT WORKS ]</span>
                    <h2 className="lndSectionTitle">Three steps. Done.</h2>
                </div>
                <div className="lndHowGrid">
                    {STEPS.map(s => (
                        <div key={s.num} className="lndHowCard">
                            <span className="lndHowNum">{s.num}</span>
                            <span className="lndHowEmoji" aria-hidden="true">{s.emoji}</span>
                            <h3 className="lndHowTitle">{s.title}</h3>
                            <p className="lndHowBody">{s.body}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Examples mosaic */}
            <section className="lndExamples">
                <div className="lndSectionHead">
                    <span className="lndEyebrow">[ EXAMPLES ]</span>
                    <h2 className="lndSectionTitle">What you can make.</h2>
                </div>
                <div className="lndMosaic">
                    {EXAMPLE_PHOTOS.map((src, i) => (
                        <div key={i} className={`lndMosaicCell lndMosaicCell--${i % 4}`}>
                            <img src={src} alt="" loading="lazy" />
                        </div>
                    ))}
                </div>
                <button className="lndHeroBtn lndHeroBtn--primary" onClick={goPhoto}>
                    Make your own
                    <span aria-hidden="true">→</span>
                </button>
            </section>

            {/* Features grid */}
            <section className="lndFeatures">
                <div className="lndSectionHead">
                    <span className="lndEyebrow">[ WHAT'S ON THE SITE ]</span>
                    <h2 className="lndSectionTitle">Two things, no fluff.</h2>
                </div>
                <div className="lndFeatureGrid lndFeatureGrid--2">
                    {FEATURES.map((f) => (
                        <button
                            key={f.target}
                            className={`lndFeature lndFeature--${f.tone}`}
                            onClick={() => onNavigate(f.target)}
                        >
                            <span className="lndFeatureEyebrow">{f.eyebrow}</span>
                            <h3 className="lndFeatureTitle">{f.title}</h3>
                            <p className="lndFeatureBody">{f.body}</p>
                            <span className="lndFeatureCta">
                                {f.cta} <span aria-hidden="true">→</span>
                            </span>
                        </button>
                    ))}
                </div>
            </section>

            {/* Pricing */}
            <section className="lndPricing">
                <div className="lndSectionHead">
                    <span className="lndEyebrow">[ PRICING ]</span>
                    <h2 className="lndSectionTitle">Pick your plan. <span className="lndFinalAccent">Cancel anytime.</span></h2>
                    <p className="lndPricingSub">Billed monthly. No watermarks. Cancel whenever you want.</p>
                </div>
                <div className="lndPriceGrid">
                    <div className="lndPriceCard">
                        <span className="lndPriceName">Starter</span>
                        <span className="lndPricePriceRow">
                            <span className="lndPricePrice">$9.99</span>
                        </span>
                        <span className="lndPricePerUnit">per month</span>
                        <ul className="lndPriceList">
                            <li><span className="lndUndressMark">✦</span> 50 tokens / month</li>
                            <li><span className="lndUndressMark">✦</span> ≈ 16 photos or 5 videos</li>
                            <li><span className="lndUndressMark">✦</span> No watermark</li>
                            <li><span className="lndUndressMark">✦</span> Cancel anytime</li>
                        </ul>
                        <button className="lndPriceBuy" onClick={handleBuy}>Buy Starter</button>
                    </div>
                    <div className="lndPriceCard lndPriceCard--popular">
                        <span className="lndPriceBadge">Most popular · Save 58%</span>
                        <span className="lndPriceName">Premium</span>
                        <span className="lndPricePriceRow">
                            <span className="lndPricePrice">$25</span>
                            <span className="lndPriceOld">$60</span>
                        </span>
                        <span className="lndPricePerUnit">per month</span>
                        <ul className="lndPriceList">
                            <li><span className="lndUndressMark">✦</span> 200 tokens / month</li>
                            <li><span className="lndUndressMark">✦</span> ≈ 66 photos or 20 videos</li>
                            <li><span className="lndUndressMark">✦</span> Priority queue</li>
                            <li><span className="lndUndressMark">✦</span> Cancel anytime</li>
                        </ul>
                        <button className="lndPriceBuy lndPriceBuy--primary" onClick={handleBuy}>Buy Premium</button>
                    </div>
                </div>
                <p className="lndPricingFoot">🔒 Secure checkout · Card · Apple/Google Pay · Crypto</p>
            </section>

            {/* FAQ */}
            <section className="lndFaq">
                <div className="lndSectionHead">
                    <span className="lndEyebrow">[ FAQ ]</span>
                    <h2 className="lndSectionTitle">Questions, answered.</h2>
                </div>
                <div className="lndFaqList">
                    {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
                </div>
            </section>

            {/* Final CTA */}
            <section className="lndFinal">
                <h2 className="lndFinalTitle">Stop reading. <span className="lndFinalAccent">Start generating.</span></h2>
                <button className="lndHeroBtn lndHeroBtn--primary lndHeroBtn--xl" onClick={goVideo}>
                    Generate a video
                    <span aria-hidden="true">→</span>
                </button>
                <p className="lndFinalDisclaimer">
                    18+ · All content is synthetic and AI-generated — no real persons depicted, no user uploads accepted. You are solely responsible for the content you create.
                </p>
            </section>

            {/* EU AI Act compliance plate */}
            <section className="lndCompliance">
                <span className="lndComplianceEyebrow">[ COMPLIANCE ]</span>
                <h3 className="lndComplianceTitle">
                    We comply with the <span className="lndComplianceAccent">EU AI Act</span> ban on nudifier apps.
                </h3>
                <p className="lndComplianceBody">
                    D33P does not provide image-undressing or nudifier functionality, and does not accept user-uploaded photos. Every image and video on D33P is generated from text prompts on our own infrastructure. We comply with the European Parliament's April 2026 simplification measures, including the explicit ban on nudifier applications.
                </p>
                <a
                    className="lndComplianceLink"
                    href={EU_AI_ACT_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    Read the official Parliament statement →
                </a>
            </section>

            {/* Footer */}
            <SiteFooter onNavigate={onNavigate} />

            <LoginPopup isOpen={showLogin} setIsOpen={setShowLogin} setSignedIn={setSignedIn} />
            <SubscriptionPopup isOpen={showPricing} onClose={() => setShowPricing(false)} token={token} onNeedLogin={() => { setShowPricing(false); setShowLogin(true); }} />
            <AccountPopup isOpen={showAccount} onClose={() => setShowAccount(false)} token={token} user={user} />
        </div>
    );
}
